import React, { useEffect, useRef, useState } from 'react';
import {
  type IdentityApi,
  identityApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import type {
  FieldExtensionComponentProps,
  FieldSchema,
} from '@backstage/plugin-scaffolder-react';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import {
  createFormField,
  type FormField,
} from '@backstage/plugin-scaffolder-react/alpha';
import { Alert, TextField } from '@backstage/ui';

const LOADING_TEXT = 'Loading…';
const IDENTITY_FAILED_TITLE = 'Your account could not be determined';
const IDENTITY_FAILED_DESCRIPTION =
  'Reload the page and try again. If this keeps happening, contact the portal team.';

export const currentUserFieldSchema: FieldSchema<string, unknown> =
  makeFieldSchema({
    output: z => z.string().min(1),
  });

type CurrentUserState =
  | { kind: 'failed' }
  | { kind: 'loading' }
  | { kind: 'ready'; shownName: string };

/** The sign-in profile's display name; undefined when it is missing or the lookup fails. */
async function loadDisplayName(
  identityApi: IdentityApi,
): Promise<string | undefined> {
  try {
    const profile = await identityApi.getProfileInfo();
    return profile.displayName;
  } catch {
    return undefined;
  }
}

/**
 * Holds and shows the signed-in user. The value is display-only: it is
 * written to the form data so the filler sees who the request is for, but an
 * action must take the user from `ctx.user`, never from this value.
 *
 * The input is disabled and carries no required marker: the user cannot edit
 * it, and the template's `required` still blocks submission until the
 * signed-in user is written.
 *
 * The name shown is the sign-in profile's display name, else the entity ref,
 * as the user settings page does. Guest sign-in has no display name, and a
 * missing name does not change who the request runs as, so neither a missing
 * name nor a failed profile lookup is reported.
 */
export function CurrentUserFieldComponent(
  props: FieldExtensionComponentProps<string, unknown>,
): React.ReactElement {
  const identityApi = useApi(identityApiRef);
  const [state, setState] = useState<CurrentUserState>({ kind: 'loading' });

  // `onChange` is a new function on every render; the load below must run once.
  const onChangeRef = useRef(props.onChange);
  onChangeRef.current = props.onChange;
  // The value the form arrived with, such as one from a `formData` URL query.
  const initialValueRef = useRef(props.formData);

  useEffect(() => {
    const controller = new AbortController();
    // A call, not a property read: a narrowed `signal.aborted` would look
    // constant across the awaits below.
    const isAborted = (): boolean => controller.signal.aborted;

    // Clear a pre-filled value before the first await, so a required field
    // blocks submission until the signed-in user is written, and stays
    // blocked if the identity cannot be determined.
    if (initialValueRef.current !== undefined) {
      const cleared: string | undefined = undefined;
      onChangeRef.current(cleared);
    }

    async function load(): Promise<void> {
      // The profile lookup runs beside the identity lookup, and its failure
      // only drops the display name. The form value comes from the identity.
      const profileDisplayName = loadDisplayName(identityApi);
      let userEntityRef: string;
      try {
        ({ userEntityRef } = await identityApi.getBackstageIdentity());
      } catch {
        if (isAborted()) return;
        setState({ kind: 'failed' });
        return;
      }
      if (isAborted()) return;
      // Always overwrite: a value that arrived through a `formData` URL
      // query must not stand in for the signed-in user.
      onChangeRef.current(userEntityRef);

      const displayName = await profileDisplayName;
      if (isAborted()) return;
      setState({
        kind: 'ready',
        shownName: displayName || userEntityRef,
      });
    }

    void load();
    return () => controller.abort();
  }, [identityApi]);

  if (state.kind === 'failed') {
    return (
      <Alert
        description={IDENTITY_FAILED_DESCRIPTION}
        role="alert"
        status="danger"
        title={IDENTITY_FAILED_TITLE}
      />
    );
  }

  const title =
    typeof props.schema.title === 'string' ? props.schema.title : undefined;
  const description =
    typeof props.schema.description === 'string'
      ? props.schema.description
      : undefined;
  const value = state.kind === 'ready' ? state.shownName : LOADING_TEXT;

  return (
    <TextField
      isDisabled
      description={description}
      label={title}
      value={value}
    />
  );
}

export const CurrentUserField: FormField = createFormField({
  component: CurrentUserFieldComponent,
  name: 'CurrentUserField',
  schema: currentUserFieldSchema,
});
